import { useContext } from 'react'
import classnames from 'classnames/bind'
import { useMutation } from 'react-query'
import { useTranslation } from 'next-i18next'
import { isEmpty } from 'lodash-es'
import { useAppSelector } from '@/redux/hooks'

// Libs
import { setRoomProductPublish } from '@/api/setRoomProductPublish'
import { setRoomProductTop } from '@/api/setRoomProductTop'
import getDisplayPrice from '@/methods/getDisplayPrice'

// Components
import Image from '@/components/common/Image'
import Button from '@/components/common/Button'
import Icon from '@/assets/icons'
import AdaptiveButton from '@/components/common/AdaptiveButton'

// Constants
import { isLiveMallClub } from '@/constants/systemType'

// Methods
import { hasDoubleEleven } from '@/methods/badge/hasDoubleEleven'

// Types
import { IRoomProductItem } from '@/types/room'

// Contexts
import { LiveRoomContext } from '@/components/pages/LiveRoom/index'
import { LiveRoomProductContext } from '@/components/pages/LiveRoomProduct/index'

// Styles
import styles from './style.module.scss'
import { safeUrl } from '@/methods/url/safeUrl'

const cx = classnames.bind(styles)

type HostProductProps = {
  className?: string
  roomId: string
  productInfo: IRoomProductItem
  sortName: string
  isLive: boolean
  isTop: boolean
  isDel: boolean
  productUrl?: string
}

const HostProduct = (props: HostProductProps) => {
  const { className, roomId, productInfo, sortName, isLive, isTop, isDel, productUrl = '' } = props

  const { t } = useTranslation()
  const memberData = useAppSelector((state) => state.member?.memberData)
  const roomData = useAppSelector((state) => state.room?.roomData)

  const { productName, productDesc, images, tags, productId, minPrice, minLivePrice, minOrigPrice } = productInfo

  const setRoomProductPublishMutation = useMutation(setRoomProductPublish)
  const setRoomProductTopMutation = useMutation(setRoomProductTop)

  const liveRoomContext: any = useContext(LiveRoomContext)
  const liveRoomProductContext: any = useContext(LiveRoomProductContext)

  const { isValidBrandName } = liveRoomContext
  const { isRoomProductDataFetching, refetchRoomProductData } = liveRoomProductContext

  const { priceLabel, displayPrice, displayOrigPrice } = getDisplayPrice({ origPrice: minOrigPrice, minPrice, livePrice: minLivePrice })
  const isDataFetched = !isEmpty(roomData.roomId) && memberData.isFetched
  const isHost = memberData?.memberInfoId === roomData?.memberInfoId

  const handleProductTop = async (e: any) => {
    e.stopPropagation()

    if (isTop) {
      await setRoomProductTopMutation.mutateAsync({ roomId, remove: [productId] })
    } else {
      await setRoomProductTopMutation.mutateAsync({ roomId, add: [productId] })
    }

    await refetchRoomProductData()
  }

  const handleProductOff = async (e: any) => {
    e.stopPropagation()

    await setRoomProductPublishMutation.mutateAsync({ roomId, remove: [productId] })
    await refetchRoomProductData()
  }

  const handleProductClick = () => {
    if (isValidBrandName && isDataFetched && !isHost) {
      handleExternalProductDataClick()
    }
  }

  const handleExternalProductDataClick = () => {
    if (!isEmpty(productUrl)) {
      window.open(safeUrl(productUrl))
    }
  }

  return (
    <div onClick={handleProductClick} className={cx('product', className)}>
      <div className={cx('product-image')}>
        {images?.length > 0 && <Image src={images[0]} width={80} height={80} style={{ objectFit: 'contain' }} alt='product image' />}

        <div className={cx('product-image-sort')}>
          {isTop && (
            <div className={cx('product-image-sort-top')}>
              <Icon.Pin />
            </div>
          )}
          <span className={cx('product-image-sort-name')}>{sortName}</span>
        </div>

        {!isEmpty(tags) && tags?.length > 0 && hasDoubleEleven(tags) && (
          <div className={cx('product-image__badge-double-11')}>
            <Image src='/images/badge-double-11.png' fill alt='' />
          </div>
        )}
      </div>

      <div className={cx('product-content')}>
        <p className={cx('product-content-name')}>{productName}</p>
        <p className={cx('product-content-description')}>{productDesc}</p>

        <div className={cx('product-content-price')}>
          <p className={cx('product-content-price-title')}>{t(priceLabel)}</p>
          {isLive && <p className={cx('product-content-price-origin')}>{`${isLiveMallClub ? 'JPY ' : '$'}${displayOrigPrice}`}</p>}
          <p className={cx('product-content-price-number')}>{`${isLiveMallClub ? 'JPY ' : '$'}${displayPrice}`}</p>
        </div>

        <div className={cx('product-content__action')}>
          {isValidBrandName ? (
            <>
              {isDataFetched && !isHost && (
                <Button
                  className={cx('product-content__action-button', { 'product-content__action-button--external-product': isValidBrandName })}
                  size='sm'
                >
                  {t('Common_BuyNow')}
                </Button>
              )}
            </>
          ) : (
            <>
              {!isDel && (
                <AdaptiveButton
                  className={cx('product-content__action-button')}
                  size='sm'
                  onClick={handleProductTop}
                  throttleDuration={0}
                  disabled={isRoomProductDataFetching}
                >
                  {isTop ? (
                    <div className={cx('product-content__action-button-top')}>
                      <div className={cx('product-content__action-button-top-icon')}>
                        <Icon.Pin />
                      </div>
                      <p>{t('LiveRoom_Pined')}</p>
                    </div>
                  ) : (
                    t('LiveRoom_Pin')
                  )}
                </AdaptiveButton>
              )}
              <AdaptiveButton
                className={cx('product-content__action-button')}
                size='sm'
                isOutline
                {...(isLiveMallClub && { theme: 'secondary' })}
                onClick={handleProductOff}
                throttleDuration={0}
                disabled={isRoomProductDataFetching || isDel}
              >
                {isDel ? t('LiveRoom_Removed') : t('LiveRoom_Remove')}
              </AdaptiveButton>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default HostProduct
